import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Preloader from 'components/Preloader';
import {
    CheckboxGroup, Title, Checkbox, Icon,
} from 'ui-library';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { ICON_JA_CONNECTION } from 'ui-library/components/Icon';
import { useManagerProfile } from 'prodivers/managerProfile';
import { useSinglePortfolio } from '../../context';
import { ACCESS_POLICY, PRODUCTS } from '../../../../../../../../constants/constants';

import Card from '../../../../../../../../components/Card/Card';
import ShadowAccountForm from './components/ShadowAccountForm/ShadowAccountForm';
import ShadowAccountsTable from './components/ShadowAccountsTable/ShadowAccountsTable';
import DepotInformationTable from './components/DepotInformation/DepotInformationTable';

const Details = () => {
    const { portfolioId } = useParams();
    const { t } = useTranslation();
    const { hasAccessPolicy } = useManagerProfile();

    const hasAccessToLockCodeData = hasAccessPolicy(ACCESS_POLICY.AccessLockCodes) || false;
    
    const { data: saldoData } = useGetCustomService({
        service: 'customInvestmentService',
        api: 'getKudiSaldo',
        payload: { portfolioId },
        enabled: !!portfolioId,
    });

    const {
        selectedProductSettings,
        portfolioDetail: { currency, customAttributes },
        is3B,
        mainClientData,
        jointClientData,
    } = useSinglePortfolio() as any;

    const lockCodeFlagCheckBoxData = useMemo(
        () => (customAttributes?.LockCode
            ? Object?.keys(customAttributes?.LockCode)
                ?.map((key) => ({
                    label: t(`Revocations.Label.${key}`),
                    value: key,
                    disabled: true,
                    booleanValue: customAttributes?.LockCode?.[key],
                }))
                ?.filter((option: any) => (is3B ? option.value === 'IsKesbAssistance' : true))
            : []),
        [customAttributes?.LockCode, is3B, t],
    );

    const lockCodeFlagCheckBoxDataCheckedValues = useMemo(
        () => lockCodeFlagCheckBoxData?.map((dta: any) => (dta?.booleanValue ? dta?.value : null)) || [],
        [lockCodeFlagCheckBoxData],
    );

    const productKey = useMemo(
        () => selectedProductSettings?.productKey?.toLowerCase(),
        [selectedProductSettings?.productKey],
    );

    const dataPerType = useMemo(() => {
        if (productKey === PRODUCTS.aaa) return { shadowAccountForm: { title: t('shadowAccount.saldo.title3a') }, shadowAccountTable: { title: t('shadowAccount.notes.title3a') } };
        if (productKey === PRODUCTS.fz) return { shadowAccountForm: { title: t('shadowAccount.saldo.titleFz') }, shadowAccountTable: { title: t('shadowAccount.notes.titleFz') } };
        if (productKey === PRODUCTS.fzp) return { shadowAccountForm: { title: t('shadowAccount.saldo.titleFzp') }, shadowAccountTable: { title: t('shadowAccount.notes.titleFzp') } };
        if (productKey === PRODUCTS.aaap) return { shadowAccountForm: { title: t('shadowAccount.saldo.title3ap') }, shadowAccountTable: { title: t('shadowAccount.notes.title3ap') } };

        return undefined;
    }, [t, productKey]);

    return (
        <Preloader isLoading={false} error={undefined}>
            <div className="container" style={{ position: 'relative' }}>
                <div className="fi-verticals portfolio-details">
                    {!is3B && (
                        <>
                            <Card title={dataPerType?.shadowAccountForm?.title}>
                                <ShadowAccountForm
                                    productKey={productKey}
                                    saldoData={saldoData?.data}
                                />
                            </Card>
                            <Card title={dataPerType?.shadowAccountTable?.title}>
                                <ShadowAccountsTable
                                    portfolioId={portfolioId}
                                    productKey={productKey}
                                    currency={currency}
                                />
                            </Card>
                            <Card title={t('shadowAccount.pledge.title')}>
                                <DepotInformationTable
                                    saldoData={saldoData?.data}
                                />
                            </Card>
                        </>
                    )}

                    {hasAccessToLockCodeData && (
                        <Card
                            className="portfolio-details__client_details_card"
                            title={t('shadowAccount.Sperrungen.title')}
                        >
                            <div className="mb-32">
                                {lockCodeFlagCheckBoxData?.length > 0 ? (
                                    <CheckboxGroup
                                        value={lockCodeFlagCheckBoxDataCheckedValues || []}
                                        options={[...lockCodeFlagCheckBoxData]}
                                        error={null}
                                        size="big"
                                        className="lock-code-flags align-start"
                                        horizontal
                                    />
                                ) : ('')}
                            </div>

                            <div>
                                <h2>{mainClientData?.clientDisplayName}</h2>
                                <Checkbox
                                    id="lockcode-at-lpz"
                                    checked={mainClientData?.isLockCodeChecked}
                                    disabled
                                    label={t('LockCodeCheckBox.Label')}
                                />

                                {mainClientData?.isLockCodeChecked && (
                                    <div className="portfolio-details__client_data_table mt-16">
                                        <div className="portfolio-details__client_data_table_item">
                                            <Title type="5">{t('LockCodeByLPZ.Table.LockCode')}</Title>
                                            <p>{mainClientData?.blockingCode}</p>
                                        </div>
                                        <div className="portfolio-details__client_data_table_item">
                                            <Title type="5">{t('LockCodeByLPZ.Table.Reason')}</Title>
                                            <p>{mainClientData?.grund || ''}</p>
                                        </div>
                                        <div className="portfolio-details__client_data_table_item">
                                            <Title type="5">{t('LockCodeByLPZ.Table.DeliveryType')}</Title>
                                            <p>{mainClientData?.shippingMethod || ''}</p>
                                        </div>
                                        <div className="portfolio-details__client_data_table_item">
                                            <Title type="5">{t('LockCodeByLPZ.Table.RefferenceNumber')}</Title>
                                            <p>{mainClientData?.refNumber || ''}</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {jointClientData && (
                                <div className="mt-24">
                                    <h2 className="details__client_data_table_ja_title">
                                        {jointClientData?.clientDisplayName}
                                        <Icon type={ICON_JA_CONNECTION} size={24} />
                                    </h2>
                                    <Checkbox
                                        id="lockcode-at-lpz"
                                        checked={jointClientData?.isLockCodeChecked}
                                        disabled
                                        label={t('LockCodeCheckBox.Label')}
                                    />

                                    {jointClientData?.isLockCodeChecked && (
                                        <div className="portfolio-details__client_data_table mt-16">
                                            <div className="portfolio-details__client_data_table_item">
                                                <Title type="5">{t('LockCodeByLPZ.Table.LockCode')}</Title>
                                                <p>{jointClientData?.blockingCode}</p>
                                            </div>
                                            <div className="portfolio-details__client_data_table_item">
                                                <Title type="5">{t('LockCodeByLPZ.Table.Reason')}</Title>
                                                <p>{jointClientData?.grund || ''}</p>
                                            </div>
                                            <div className="portfolio-details__client_data_table_item">
                                                <Title type="5">{t('LockCodeByLPZ.Table.DeliveryType')}</Title>
                                                <p>{jointClientData?.shippingMethod || ''}</p>
                                            </div>
                                            <div className="portfolio-details__client_data_table_item">
                                                <Title type="5">{t('LockCodeByLPZ.Table.RefferenceNumber')}</Title>
                                                <p>{jointClientData?.refNumber || ''}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card>
                    )}
                </div>
            </div>
        </Preloader>
    );
};

export default Details;
